import type { IReactionDisposer } from "mobx"
import type { Address } from "viem"
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx"

import type { IEmissionsConfig, IGaugeRepository } from "@/domains/gauge"
import type { ILockLookup } from "@/domains/lock/interfaces"
import type { SelectedPowerStore } from "@/domains/selected-power"
import type { Sort, SortDirection, SortType } from "@/domains/table-processor"
import type { IVoteStore } from "@/domains/vote"
import type { StrategyManagerStore, VoteOptimizationStrategyType } from "@/domains/vote-optimizer"
import { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { LcAddress } from "@/domains/core"

import { TableProcessor } from "@/domains/table-processor"

import type { IMinMaxFilter, IVotePageStore } from "./interfaces"
import { VotingGauge } from "./voting-gauge"

// SortBy param sources
export interface SortByParamSources {
  urlParam: Sort | undefined
  localstorage: Sort | undefined
  default: Sort
  ui: Sort | undefined
}

// Filter param sources
export interface FilterParamSources {
  urlParam: string | number | boolean | undefined
  localstorage: string | number | boolean | undefined
  default: string | number | boolean
  ui: string | number | boolean | undefined
}

export class VotePageStore implements IVotePageStore {
  // Selected power store - injected from factory
  selectedPowerStore?: SelectedPowerStore

  // Lock lookup and emissions config - injected from factory (optional for now)
  lockLookup?: ILockLookup
  emissionsConfig?: IEmissionsConfig

  // Single source of truth - all gauges with vote allocations
  votingGauges: VotingGauge[] = []

  sortByParamSources: SortByParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: { key: "totalBribeValueUsd", direction: "desc", type: "number" },
    ui: undefined,
  }

  // Filter param sources
  searchParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "",
    ui: undefined,
  }

  votedOnlyParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  minimumTvlParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 0,
    ui: undefined,
  }

  minimumBribeParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 0,
    ui: undefined,
  }

  maximumBribeParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 0,
    ui: undefined,
  }

  minimumDollarPerVoteParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 0,
    ui: undefined,
  }

  maxLockOnVoteParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  // Drawer state
  isCastYourVoteDrawerOpen: boolean = false

  // Max Lock toggle state
  maxLockEnabled: boolean = false

  // SuperVoter access state
  hasSuperVoterAccess: boolean = false

  // SuperVoter strategy state
  lastAppliedStrategy: VoteOptimizationStrategyType | null = null
  previousAppliedStrategy: VoteOptimizationStrategyType | null = null
  lastAppliedTokenStrategy: LcAddress | null = null
  previousAppliedTokenStrategy: LcAddress | null = null
  lastTokenStrategyEstimatedAmount: number = 0

  // SuperVoter optimization engine selection (dropdown)
  selectedEngineType: "optimization" | "token" | null = null
  selectedOptimizationStrategy: VoteOptimizationStrategyType | null = null
  selectedTokenStrategy: LcAddress | null = null

  // Available Revenue Sources search (separate from main vote page search)
  availableRevenueSourcesSearch: string = ""

  // APR filter state for available revenue sources
  availableRevenueSourcesAprFilterValue: number = 0
  availableRevenueSourcesAprFilterMode: "min" | "max" = "min"

  // TVL filter state for available revenue sources (default 0 - show new protocols with low TVL)
  availableRevenueSourcesTvlFilterValue: number = 0
  availableRevenueSourcesTvlFilterMode: "min" | "max" = "min"

  // Revenue filter state for available revenue sources (default 0 - show new protocols with low revenue)
  availableRevenueSourcesRevenueFilterValue: number = 0
  availableRevenueSourcesRevenueFilterMode: "min" | "max" = "min"

  // List view state for available revenue sources (default: true = list view)
  listViewParamSources: FilterParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: true,
    ui: undefined,
  }

  // Sort state for available revenue sources table (list view)
  availableRevenueSourcesSortByParamSources: SortByParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: { key: "fees", direction: "desc", type: "number" },
    ui: undefined,
  }

  // Reaction disposer for gauge repository watching
  private gaugeRepositoryReactionDisposer?: IReactionDisposer

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private chainSlug: string,
    private gaugeRepository: IGaugeRepository,
    private voteStore: IVoteStore,
    private nativeTokenSymbol: string,
    private wrappedNativeTokenSymbol: string,
  ) {
    makeObservable(this, {
      votingGauges: observable,
      sortByParamSources: observable,
      searchParamSources: observable,
      votedOnlyParamSources: observable,
      minimumTvlParamSources: observable,
      minimumBribeParamSources: observable,
      maximumBribeParamSources: observable,
      minimumDollarPerVoteParamSources: observable,
      maxLockOnVoteParamSources: observable,
      isCastYourVoteDrawerOpen: observable,
      maxLockEnabled: observable,
      hasSuperVoterAccess: observable,
      lastAppliedStrategy: observable,
      previousAppliedStrategy: observable,
      lastAppliedTokenStrategy: observable,
      previousAppliedTokenStrategy: observable,
      lastTokenStrategyEstimatedAmount: observable,
      selectedEngineType: observable,
      selectedOptimizationStrategy: observable,
      selectedTokenStrategy: observable,
      availableRevenueSourcesSearch: observable,
      availableRevenueSourcesAprFilterValue: observable,
      availableRevenueSourcesAprFilterMode: observable,
      availableRevenueSourcesTvlFilterValue: observable,
      availableRevenueSourcesTvlFilterMode: observable,
      availableRevenueSourcesRevenueFilterValue: observable,
      availableRevenueSourcesRevenueFilterMode: observable,
      listViewParamSources: observable,
      availableRevenueSourcesSortByParamSources: observable,
      selectedNfts: computed,
      sortBy: computed,
      search: computed,
      votedOnly: computed,
      minimumTvl: computed,
      minimumBribe: computed,
      maximumBribe: computed,
      minimumDollarPerVote: computed,
      maxLockOnVote: computed,
      selectedVotePower: computed,
      superVoterGauges: computed,
      superVoterTotalExpectedIncome: computed,
      superVoterPoolCount: computed,
      superVoterRevenueSources: computed,
      superVoterTotalAllocation: computed,
      transactionFailureRisk: computed,
      availableRevenueSources: computed,
      availableRevenueSourcesSortBy: computed,
      availableRevenueSourcesAprFilter: computed,
      availableRevenueSourcesTvlFilter: computed,
      availableRevenueSourcesRevenueFilter: computed,
      listView: computed,
      setSortByFromUrlParam: action,
      setSortByFromLocalStorage: action,
      setSortByFromUi: action,
      setSearchFromUrlParam: action,
      setSearchFromLocalStorage: action,
      setSearchFromUi: action,
      setVotedOnlyFromUrlParam: action,
      setVotedOnlyFromLocalStorage: action,
      setVotedOnlyFromUi: action,
      setMinimumTvlFromUi: action,
      setMaximumBribeFromUi: action,
      setMinimumBribeFromUi: action,
      setMinimumDollarPerVoteFromUi: action,
      setMaxLockOnVoteFromUi: action,
      openCastYourVoteDrawer: action,
      closeCastYourVoteDrawer: action,
      toggleNftSelection: action,
      removeSuperVoterPool: action,
      updateSuperVoterPoolAllocation: action,
      resetSuperVoterAllocations: action,
      castSuperVoterVote: action,
      setMaxLockEnabled: action,
      setHasSuperVoterAccess: action,
      setLastAppliedStrategy: action,
      setPreviousAppliedStrategy: action,
      setLastAppliedTokenStrategy: action,
      setPreviousAppliedTokenStrategy: action,
      applyStrategy: action,
      applyTokenStrategy: action,
      setSelectedEngine: action,
      clearSelectedEngine: action,
      setAvailableRevenueSourcesSearch: action,
      setAvailableRevenueSourcesAprFilterValue: action,
      setAvailableRevenueSourcesAprFilterMode: action,
      setAvailableRevenueSourcesTvlFilterValue: action,
      setAvailableRevenueSourcesTvlFilterMode: action,
      setAvailableRevenueSourcesRevenueFilterValue: action,
      setAvailableRevenueSourcesRevenueFilterMode: action,
      setListViewFromUrlParam: action,
      setListViewFromLocalStorage: action,
      setListViewFromUi: action,
      setAvailableRevenueSourcesSortByFromUi: action,
      initializeVotingGauges: action,
    })
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
      this.initializeAutoSelect()
      this.initializeVotingGauges()
    })

    // Set up reaction to watch gauge repository and update votingGauges when it changes
    this.setupGaugeRepositoryReaction()
  }

  /**
   * Set up MobX reaction to watch gauge repository and automatically update votingGauges
   * when gauges are added or removed from the repository
   */
  private setupGaugeRepositoryReaction(): void {
    // Dispose any existing reaction
    if (this.gaugeRepositoryReactionDisposer) {
      this.gaugeRepositoryReactionDisposer()
    }

    // Watch the gauge repository's observable gauges Map
    // Access the Map directly to ensure MobX tracks it properly
    // Cast through unknown to access the private gauges property (it's observable, so MobX will track it)
    const repository = this.gaugeRepository as unknown as { gauges: Map<string, unknown> }

    this.gaugeRepositoryReactionDisposer = reaction(
      () => {
        // Track the Map size and keys to detect additions/removals
        // Accessing .size and iterating over keys ensures MobX tracks the Map
        const map = repository.gauges
        return {
          size: map.size,
          keys: Array.from(map.keys()).sort().join(","),
        }
      },
      () => {
        // When repository changes, reinitialize votingGauges
        // This preserves existing voteValues by checking voteStore
        runInAction(() => {
          this.initializeVotingGauges()
        })
      },
    )
  }

  /**
   * Initialize votingGauges array from gaugeRepository
   * Called during initialization and when gauges are added/removed
   *
   * Note: VotingGauge instances observe selectedPowerStore, so stats will automatically
   * recompute when selectedVotePower or selectedNfts change. We only need to recreate
   * VotingGauge instances when gauges are added/removed from the repository.
   */
  initializeVotingGauges(): void {
    const gauges = this.gaugeRepository.getAll()
    const voteValues = this.voteStore.voteValues

    // Create VotingGauge for each gauge
    // Initialize voteValue from voteStore if it exists
    this.votingGauges = gauges.map((gauge) => {
      const votingGauge = new VotingGauge(gauge, this.selectedPowerStore, this.lockLookup, this.emissionsConfig)

      // Initialize voteValue from voteStore if it exists
      const revenueSourceAddress = gauge.revenueSourceAddress.toString()
      const vote = voteValues[revenueSourceAddress]
      if (vote) {
        votingGauge.voteValue = vote.value
      }

      return votingGauge
    })
  }

  private initializeFromStorage(): void {
    // Note: selectedNfts are no longer stored in localStorage to prevent cross-wallet persistence
    // They are only stored in URL params, which is wallet-agnostic and shareable

    // Initialize sortBy from localStorage
    const localSortBy = this.storage.getItem(`${this.chainSlug}-vote-sortBy`)
    if (localSortBy) {
      try {
        const parsedSortBy = JSON.parse(localSortBy) as Sort
        this.setSortByFromLocalStorage(parsedSortBy)
      } catch {
        // Invalid JSON, ignore
      }
    }

    // Initialize listView from localStorage
    const localListView = this.storage.getItem(`${this.chainSlug}-vote-listView`)
    if (localListView) {
      const parsedListView = localListView === "true"
      this.setListViewFromLocalStorage(parsedListView)
    }

    // Initialize availableRevenueSources sort from localStorage
    const localAvailableSortBy = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-sortBy`)
    if (localAvailableSortBy) {
      try {
        const parsed = JSON.parse(localAvailableSortBy) as Sort
        if (parsed.key && parsed.direction && parsed.type) {
          this.availableRevenueSourcesSortByParamSources.localstorage = parsed
        }
      } catch {
        // Invalid JSON, ignore
      }
    }

    // Initialize availableRevenueSources search from localStorage
    const localAvailableSearch = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-search`)
    if (localAvailableSearch !== null) {
      this.availableRevenueSourcesSearch = localAvailableSearch
    }

    // Initialize availableRevenueSources APR filter from localStorage
    const localAprValue = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-aprValue`)
    if (localAprValue !== null) {
      const parsed = Number(localAprValue)
      if (!isNaN(parsed)) {
        this.availableRevenueSourcesAprFilterValue = parsed
      }
    }
    const localAprMode = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-aprMode`)
    if (localAprMode === "min" || localAprMode === "max") {
      this.availableRevenueSourcesAprFilterMode = localAprMode
    }

    // Initialize availableRevenueSources TVL filter from localStorage
    const localTvlValue = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-tvlValue`)
    if (localTvlValue !== null) {
      const parsed = Number(localTvlValue)
      if (!isNaN(parsed)) {
        this.availableRevenueSourcesTvlFilterValue = parsed
      }
    }
    const localTvlMode = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-tvlMode`)
    if (localTvlMode === "min" || localTvlMode === "max") {
      this.availableRevenueSourcesTvlFilterMode = localTvlMode
    }

    // Initialize availableRevenueSources Revenue filter from localStorage
    const localRevenueValue = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-revenueValue`)
    if (localRevenueValue !== null) {
      const parsed = Number(localRevenueValue)
      if (!isNaN(parsed)) {
        this.availableRevenueSourcesRevenueFilterValue = parsed
      }
    }
    const localRevenueMode = this.storage.getItem(`${this.chainSlug}-vote-availableRevenueSources-revenueMode`)
    if (localRevenueMode === "min" || localRevenueMode === "max") {
      this.availableRevenueSourcesRevenueFilterMode = localRevenueMode
    }
  }

  public initializeFromUrlParams(): void {
    // Note: selectedNfts are no longer read from URL params to prevent recursive loops
    // They are only stored in SelectedPowerStore state

    // Initialize sortBy from URL params
    const urlSortKey = this.urlParams.getParam("sortKey")
    const urlSortDir = this.urlParams.getParam("sortDir")
    const urlSortType = this.urlParams.getParam("sortType")

    if (urlSortKey && urlSortDir && urlSortType) {
      const sortBy: Sort = {
        key: urlSortKey,
        direction: urlSortDir as SortDirection,
        type: urlSortType as SortType,
      }
      this.setSortByFromUrlParam(sortBy)
    }

    // Initialize filters from URL params
    const urlSearch = this.urlParams.getParam("search")
    if (urlSearch) {
      this.setSearchFromUrlParam(urlSearch)
    }

    const urlVotedOnly = this.urlParams.getParam("voted")
    if (urlVotedOnly) {
      this.setVotedOnlyFromUrlParam(urlVotedOnly === "true")
    }

    const urlMinTvl = this.urlParams.getParam("minTvl")
    if (urlMinTvl) {
      this.setMinimumTvlFromUi(Number(urlMinTvl))
    }

    const urlMinBribe = this.urlParams.getParam("minBribe")
    if (urlMinBribe) {
      this.setMinimumBribeFromUi(Number(urlMinBribe))
    }

    const urlMaxBribe = this.urlParams.getParam("maxBribe")
    if (urlMaxBribe) {
      this.setMaximumBribeFromUi(Number(urlMaxBribe))
    }

    const urlMinDollarPerVote = this.urlParams.getParam("minDollarPerVote")
    if (urlMinDollarPerVote) {
      this.setMinimumDollarPerVoteFromUi(Number(urlMinDollarPerVote))
    }

    const urlMaxLockOnVote = this.urlParams.getParam("maxLockOnVote")
    if (urlMaxLockOnVote) {
      this.setMaxLockOnVoteFromUi(urlMaxLockOnVote === "true")
    }

    // listView is persisted to localStorage only, not URL (per vote page filter memory design)
  }

  // Delegate to SelectedPowerStore
  get selectedNfts(): number[] {
    return this.selectedPowerStore?.selectedNftIds ?? []
  }

  /**
   * Initialize auto-select for single NFT
   * Called after SelectedPowerStore is injected
   */
  private initializeAutoSelect(): void {
    if (!this.selectedPowerStore) {
      return
    }
    // Check if we should auto-select a single NFT
    const autoSelectedId = this.selectedPowerStore.autoSelectedNftId
    if (autoSelectedId !== null) {
      this.selectedPowerStore.setSelectedNfts([autoSelectedId])
    }
  }

  get sortBy(): Sort {
    return (
      this.sortByParamSources.ui ??
      this.sortByParamSources.urlParam ??
      this.sortByParamSources.localstorage ??
      this.sortByParamSources.default
    )
  }

  get search(): string {
    return (this.searchParamSources.ui ??
      this.searchParamSources.urlParam ??
      this.searchParamSources.localstorage ??
      this.searchParamSources.default) as string
  }

  get votedOnly(): boolean {
    return (this.votedOnlyParamSources.ui ??
      this.votedOnlyParamSources.urlParam ??
      this.votedOnlyParamSources.localstorage ??
      this.votedOnlyParamSources.default) as boolean
  }

  get listView(): boolean {
    return (this.listViewParamSources.ui ??
      this.listViewParamSources.urlParam ??
      this.listViewParamSources.localstorage ??
      this.listViewParamSources.default) as boolean
  }

  get availableRevenueSourcesSortBy(): Sort {
    return (
      this.availableRevenueSourcesSortByParamSources.ui ??
      this.availableRevenueSourcesSortByParamSources.urlParam ??
      this.availableRevenueSourcesSortByParamSources.localstorage ??
      this.availableRevenueSourcesSortByParamSources.default
    )
  }

  get minimumTvl(): number {
    return (this.minimumTvlParamSources.ui ??
      this.minimumTvlParamSources.urlParam ??
      this.minimumTvlParamSources.localstorage ??
      this.minimumTvlParamSources.default) as number
  }

  get minimumBribe(): number {
    return (this.minimumBribeParamSources.ui ??
      this.minimumBribeParamSources.urlParam ??
      this.minimumBribeParamSources.localstorage ??
      this.minimumBribeParamSources.default) as number
  }

  get maximumBribe(): number {
    return (this.maximumBribeParamSources.ui ??
      this.maximumBribeParamSources.urlParam ??
      this.maximumBribeParamSources.localstorage ??
      this.maximumBribeParamSources.default) as number
  }

  get minimumDollarPerVote(): number {
    return (this.minimumDollarPerVoteParamSources.ui ??
      this.minimumDollarPerVoteParamSources.urlParam ??
      this.minimumDollarPerVoteParamSources.localstorage ??
      this.minimumDollarPerVoteParamSources.default) as number
  }

  get maxLockOnVote(): boolean {
    return (this.maxLockOnVoteParamSources.ui ??
      this.maxLockOnVoteParamSources.urlParam ??
      this.maxLockOnVoteParamSources.localstorage ??
      this.maxLockOnVoteParamSources.default) as boolean
  }

  get selectedVotePower(): number {
    // Delegate to SelectedPowerStore
    return this.selectedPowerStore?.selectedVotePower ?? 0
  }

  // Drawer actions
  openCastYourVoteDrawer(): void {
    this.isCastYourVoteDrawerOpen = true
  }

  closeCastYourVoteDrawer(): void {
    this.isCastYourVoteDrawerOpen = false
  }

  /**
   * Computed: SuperVoter gauges - just a filter of votingGauges with voteValue > 0
   */
  get superVoterGauges(): VotingGauge[] {
    return this.votingGauges.filter((vg) => vg.isInSuperVoter)
  }

  // SortBy setters
  setSortByFromUrlParam(value: Sort | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.urlParam?.key === value.key &&
      this.sortByParamSources.urlParam?.direction === value.direction &&
      this.sortByParamSources.urlParam?.type === value.type
    )
      return
    this.sortByParamSources.urlParam = value
  }

  setSortByFromLocalStorage(value: Sort | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.localstorage?.key === value.key &&
      this.sortByParamSources.localstorage?.direction === value.direction &&
      this.sortByParamSources.localstorage?.type === value.type
    )
      return
    this.sortByParamSources.localstorage = value
  }

  setSortByFromUi(value: Sort | undefined): void {
    if (!value) return

    if (
      this.sortByParamSources.ui?.key === value.key &&
      this.sortByParamSources.ui?.direction === value.direction &&
      this.sortByParamSources.ui?.type === value.type
    )
      return
    this.sortByParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-vote-sortBy`, JSON.stringify(value))
    this.urlParams.setParam("sortKey", value.key)
    this.urlParams.setParam("sortDir", value.direction)
    this.urlParams.setParam("sortType", value.type)
  }

  // Filter setters
  setSearchFromUrlParam(value: string | undefined): void {
    if (value === undefined) return
    if (this.searchParamSources.urlParam === value) return
    this.searchParamSources.urlParam = value
  }

  setSearchFromLocalStorage(value: string | undefined): void {
    if (value === undefined) return
    if (this.searchParamSources.localstorage === value) return
    this.searchParamSources.localstorage = value
  }

  setSearchFromUi(value: string | undefined): void {
    if (value === undefined) return
    if (this.searchParamSources.ui === value) return
    this.searchParamSources.ui = value
    this.urlParams.setParam("search", value)
  }

  setVotedOnlyFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.votedOnlyParamSources.urlParam === value) return
    this.votedOnlyParamSources.urlParam = value
  }

  setVotedOnlyFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.votedOnlyParamSources.localstorage === value) return
    this.votedOnlyParamSources.localstorage = value
  }

  setVotedOnlyFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.votedOnlyParamSources.ui === value) return
    this.votedOnlyParamSources.ui = value
    this.urlParams.setParam("voted", value.toString())
  }

  setMinimumTvlFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.minimumTvlParamSources.ui === value) return
    this.minimumTvlParamSources.ui = value
    this.urlParams.setParam("minTvl", value.toString())
  }

  setMinimumBribeFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.minimumBribeParamSources.ui === value) return
    this.minimumBribeParamSources.ui = value
    this.urlParams.setParam("minBribe", value.toString())
  }

  setMaximumBribeFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.maximumBribeParamSources.ui === value) return
    this.maximumBribeParamSources.ui = value
    this.urlParams.setParam("maxBribe", value.toString())
  }

  setMinimumDollarPerVoteFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.minimumDollarPerVoteParamSources.ui === value) return
    this.minimumDollarPerVoteParamSources.ui = value
    this.urlParams.setParam("minDollarPerVote", value.toString())
  }

  setMaxLockOnVoteFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.maxLockOnVoteParamSources.ui === value) return
    this.maxLockOnVoteParamSources.ui = value
    this.urlParams.setParam("maxLockOnVote", value.toString())
  }

  // Convenience methods
  toggleNftSelection(nftId: number): void {
    // Delegate to SelectedPowerStore
    this.selectedPowerStore?.toggleNftSelection(nftId)
  }

  clearSelectedNfts(): void {
    this.selectedPowerStore?.setSelectedNfts([])
  }

  setSelectedNfts(value: number[]): void {
    this.selectedPowerStore?.setSelectedNfts(value)
  }

  // SuperVoter computed properties
  get superVoterTotalExpectedIncome(): number {
    return this.superVoterGauges.reduce((sum, vg) => sum + vg.stats.newExpectedIncome, 0)
  }

  get superVoterPoolCount(): number {
    return this.superVoterGauges.length
  }

  /**
   * Computed: SuperVoter revenue sources
   * Returns VotingGauge[] which implements IRevenueSourceCard via facade properties
   * This preserves full MobX reactivity by passing the actual VotingGauge instances
   */
  get superVoterRevenueSources(): VotingGauge[] {
    return this.superVoterGauges
  }

  get superVoterTotalAllocation(): number {
    return this.superVoterGauges.reduce((sum, vg) => sum + vg.voteValue, 0)
  }

  /**
   * Transaction Failure Risk - Calculates the likelihood that a vote transaction will fail
   * due to block size limits based on the combination of selected NFTs and pools.
   *
   * Empirical thresholds based on observed failures:
   * - 10 NFTs × 9 pools (90) = success
   * - 9 NFTs × 10 pools (90) = success
   * - 9 NFTs × 11 pools (99) = failure
   * - 9 NFTs × 12 pools (108) = failure
   * - 10 NFTs × 10 pools (100) = failure
   * - 10 NFTs × 12 pools (120) = failure
   * - 10 NFTs × 16 pools (160) = failure
   * - 6 NFTs × 16 pools (96) = success
   * - 6 NFTs × 17 pools (102) = success
   *
   * Observation: Fewer NFTs allow more total operations. 6 NFTs can handle 102 operations,
   * while 9 NFTs fail at 99 operations. This suggests NFTs have a higher gas cost per operation.
   *
   * Weighted formula: weightedOperations = nftCount * poolCount * (1 + (nftCount - 1) * 0.05)
   * This applies a subtle penalty as NFT count increases, reflecting higher gas usage per NFT.
   *
   * Risk levels are scaled relative to a threshold of ~90-95 weighted operations:
   * - Low (0-60 weighted ops): Green, 0-25% bar (safe zone, well below limit)
   * - Moderate (61-80 weighted ops): Yellow, 25-50% bar (caution zone)
   * - High (81-95 weighted ops): Orange, 50-75% bar (approaching limit)
   * - Critical (96+ weighted ops): Red, 75-100% bar (will fail)
   */
  get transactionFailureRisk(): {
    value: number // 0-100 percentage
    rank: "low" | "moderate" | "high" | "critical"
    color: string // RGB color value
  } {
    const nftCount = this.selectedPowerStore?.selectedNftIds.length ?? 0
    const poolCount = this.superVoterRevenueSources.filter((vg) => vg.voteValue > 0).length
    const baseOperations = nftCount * poolCount

    // Weighted formula: More NFTs increase risk subtly
    // Formula: baseOperations * (1 + (nftCount - 1) * 0.05)
    // This applies a 5% penalty per NFT above 1, reflecting higher gas cost per NFT
    // Examples:
    // - 6 NFTs × 17 pools: 102 * (1 + 5 * 0.05) = 102 * 1.25 = 127.5
    // - 9 NFTs × 11 pools: 99 * (1 + 8 * 0.05) = 99 * 1.40 = 138.6
    // - 10 NFTs × 9 pools: 90 * (1 + 9 * 0.05) = 90 * 1.45 = 130.5
    const weightedOperations = baseOperations * (1 + (nftCount - 1) * 0.05)

    // Empirical thresholds based on weighted operations:
    // 6 NFTs × 17 pools (102) = 127.5 weighted = success
    // 9 NFTs × 11 pools (99) = 138.6 weighted = failure
    // Threshold appears to be around 130-135 weighted operations

    const SAFE_THRESHOLD = 80 // Well below limit
    const CAUTION_THRESHOLD = 100 // Getting close
    const WARNING_THRESHOLD = 130 // Approaching limit

    let value: number
    let rank: "low" | "moderate" | "high" | "critical"
    let color: string

    if (weightedOperations <= SAFE_THRESHOLD) {
      value = (weightedOperations / SAFE_THRESHOLD) * 25 // Scale 0-80 to 0-25%
      rank = "low"
      color = "rgb(34, 197, 94)" // green-500
    } else if (weightedOperations <= CAUTION_THRESHOLD) {
      value = 25 + ((weightedOperations - SAFE_THRESHOLD) / (CAUTION_THRESHOLD - SAFE_THRESHOLD)) * 25 // Scale 81-100 to 25-50%
      rank = "moderate"
      color = "rgb(234, 179, 8)" // yellow-500
    } else if (weightedOperations <= WARNING_THRESHOLD) {
      value = 50 + ((weightedOperations - CAUTION_THRESHOLD) / (WARNING_THRESHOLD - CAUTION_THRESHOLD)) * 25 // Scale 101-130 to 50-75%
      rank = "high"
      color = "rgb(249, 115, 22)" // orange-500
    } else {
      // 131+ weighted operations - will fail
      value = 75 + Math.min(25, ((weightedOperations - WARNING_THRESHOLD) / 30) * 25) // Scale 131+ to 75-100%
      rank = "critical"
      color = "rgb(239, 68, 68)" // red-500
    }

    return { value, rank, color }
  }

  // SuperVoter actions
  removeSuperVoterPool(poolId: string): void {
    const address = new LcAddress(poolId)
    const votingGauge = this.votingGauges.find((vg) => vg.gauge.revenueSourceAddress.equals(address))
    if (votingGauge) {
      votingGauge.setVoteValue(0)
    }
  }

  updateSuperVoterPoolAllocation(poolId: string, allocation: number): void {
    const address = new LcAddress(poolId)
    const votingGauge = this.votingGauges.find((vg) => vg.gauge.revenueSourceAddress.equals(address))
    if (votingGauge) {
      votingGauge.setVoteValue(allocation)
    }
  }

  resetSuperVoterAllocations(): void {
    this.votingGauges.forEach((vg) => vg.setVoteValue(0))
  }

  /**
   * Apply previous votes from selected NFTs
   * Aggregates last epoch votes from all selected NFTs, normalizes to 100%, and applies to vote allocations
   */
  applyPreviousVotes(): void {
    if (!this.selectedPowerStore) {
      return
    }

    const selectedNftIds = this.selectedPowerStore.selectedNftIds
    if (selectedNftIds.length === 0) {
      return
    }

    // Get ILock instances for selected NFTs
    const lockRepository = this.selectedPowerStore.activeLockRepository
    const selectedLocks = selectedNftIds
      .map((id) => lockRepository.find(id))
      .filter((lock): lock is NonNullable<typeof lock> => lock !== undefined)

    if (selectedLocks.length === 0) {
      return
    }

    // Aggregate last epoch votes by gauge address
    // Map: gaugeAddress -> sum of votePercent from all selected NFTs
    const gaugeVoteTotals = new Map<string, number>()

    for (const lock of selectedLocks) {
      for (const vote of lock.lastEpochVotes) {
        const gaugeKey = vote.gaugeAddress.toString()
        const currentTotal = gaugeVoteTotals.get(gaugeKey) ?? 0
        gaugeVoteTotals.set(gaugeKey, currentTotal + vote.votePercent)
      }
    }

    if (gaugeVoteTotals.size === 0) {
      // No last epoch votes found
      return
    }

    // Calculate total sum for normalization
    const totalSum = Array.from(gaugeVoteTotals.values()).reduce((sum, value) => sum + value, 0)

    if (totalSum === 0) {
      return
    }

    // Normalize to 100%, round to integers, and ensure total never exceeds 100%
    // First, reset all allocations
    this.resetSuperVoterAllocations()

    // Calculate normalized percentages and round to integers FIRST
    const roundedAllocations: Array<{ gaugeAddress: LcAddress; percent: number }> = []

    for (const [gaugeAddressStr, totalPercent] of gaugeVoteTotals.entries()) {
      const normalizedPercent = (totalPercent / totalSum) * 100
      // Round to integer immediately and ensure it's a whole number
      const roundedPercent = Math.round(normalizedPercent)
      const gaugeAddress = new LcAddress(gaugeAddressStr)

      // Find the revenue source (pool) that matches this gauge address
      const votingGauge = this.votingGauges.find((vg) =>
        vg.gauge.revenueSourceAddress.equals(gaugeAddress),
      )

      if (votingGauge && roundedPercent > 0) {
        // Ensure we're storing an integer
        roundedAllocations.push({ gaugeAddress, percent: Math.round(roundedPercent) })
      }
      // If gauge no longer exists, skip it (edge case handled)
    }

    if (roundedAllocations.length === 0) {
      return
    }

    // Calculate initial total - ensure all values are integers
    let currentTotal = roundedAllocations.reduce((sum, alloc) => {
      const intPercent = Math.round(alloc.percent)
      return sum + intPercent
    }, 0)

    // Ensure all percentages are integers before the loop
    for (const allocation of roundedAllocations) {
      allocation.percent = Math.round(allocation.percent)
    }

    // Loop: decrease largest by 1% until total is exactly 100%
    // All operations use integers to avoid floating point issues
    while (currentTotal > 100 && roundedAllocations.length > 0) {
      // Sort by percentage (descending) to get largest
      roundedAllocations.sort((a, b) => b.percent - a.percent)
      
      // Decrease the largest allocation by 1% (integer operation)
      const largest = roundedAllocations[0]
      if (largest && largest.percent > 0) {
        largest.percent = Math.max(0, Math.round(largest.percent - 1))
        currentTotal = Math.round(currentTotal - 1)
      } else {
        // If largest is already 0, remove it and continue
        roundedAllocations.shift()
      }
      
      // Recalculate total to ensure accuracy (using integer math)
      currentTotal = roundedAllocations.reduce((sum, alloc) => {
        return sum + Math.round(alloc.percent)
      }, 0)
    }

    // If total is less than 100%, add remainder to the largest allocation
    if (currentTotal < 100 && roundedAllocations.length > 0) {
      roundedAllocations.sort((a, b) => b.percent - a.percent)
      const remainder = 100 - currentTotal
      roundedAllocations[0]!.percent = Math.round(roundedAllocations[0]!.percent + remainder)
      currentTotal = 100
    }

    // Apply the rounded and adjusted allocations
    for (const { gaugeAddress, percent } of roundedAllocations) {
      // Final safety clamp to ensure no single value exceeds 100
      const clampedPercent = Math.min(100, Math.max(0, percent))
      const votingGauge = this.votingGauges.find((vg) =>
        vg.gauge.revenueSourceAddress.equals(gaugeAddress),
      )
      if (votingGauge) {
        votingGauge.setVoteValue(clampedPercent)
      }
    }
  }

  castSuperVoterVote(): {
    nftIds: Array<number>
    votePools: Array<Address>
    voteCounts: Array<string>
    totalVotesVoting: number
    maxLock: boolean
  } | null {
    // Validation: Check if selected locks exist
    if (!this.selectedPowerStore || this.selectedPowerStore.selectedNftIds.length === 0) {
      return null
    }

    // Get vote allocations (only pools with voteValue > 0)
    const gaugesWithVotes = this.superVoterRevenueSources.filter((vg) => vg.voteValue > 0)

    // Validation: Check if vote allocations exist
    if (gaugesWithVotes.length === 0) {
      return null
    }

    // Validation: Check if total allocation is valid (0-100%)
    const totalAllocation = this.superVoterTotalAllocation
    if (totalAllocation < 0 || totalAllocation > 100) {
      return null
    }

    // Collect selected NFT IDs
    const nftIds = this.selectedPowerStore.selectedNftIds

    // Extract pool addresses and convert vote values to basis points
    const votePools: Array<Address> = []
    const voteCounts: Array<string> = []

    for (const gauge of gaugesWithVotes) {
      votePools.push(gauge.gauge.revenueSourceAddress.toViemAddress())
      // Convert voteValue (0-100 percentage) to basis points (multiply by 100)
      voteCounts.push(String(Math.round(gauge.voteValue * 100)))
    }

    // Calculate total votes voting (sum of selected vote power)
    const selectedVotePower = this.selectedVotePower
    const totalVotesVoting = selectedVotePower

    // Get maxLock from store state
    const maxLock = this.maxLockEnabled

    return {
      nftIds,
      votePools,
      voteCounts,
      totalVotesVoting,
      maxLock,
    }
  }

  setMaxLockEnabled(enabled: boolean): void {
    this.maxLockEnabled = enabled
  }

  setHasSuperVoterAccess(hasAccess: boolean): void {
    this.hasSuperVoterAccess = hasAccess
  }

  setLastAppliedStrategy(strategy: VoteOptimizationStrategyType | null): void {
    this.lastAppliedStrategy = strategy
  }

  setPreviousAppliedStrategy(strategy: VoteOptimizationStrategyType | null): void {
    this.previousAppliedStrategy = strategy
  }

  setLastAppliedTokenStrategy(tokenAddress: LcAddress | null): void {
    this.lastAppliedTokenStrategy = tokenAddress
  }

  setPreviousAppliedTokenStrategy(tokenAddress: LcAddress | null): void {
    this.previousAppliedTokenStrategy = tokenAddress
  }

  setSelectedEngine(
    engineType: "optimization" | "token",
    optimizationId: VoteOptimizationStrategyType | undefined,
    tokenAddress: LcAddress | undefined,
  ): void {
    this.selectedEngineType = engineType
    if (engineType === "optimization" && optimizationId) {
      this.selectedOptimizationStrategy = optimizationId
      this.selectedTokenStrategy = null
    } else if (engineType === "token" && tokenAddress) {
      this.selectedTokenStrategy = tokenAddress
      this.selectedOptimizationStrategy = null
    } else {
      this.selectedOptimizationStrategy = null
      this.selectedTokenStrategy = null
    }
  }

  clearSelectedEngine(): void {
    this.selectedEngineType = null
    this.selectedOptimizationStrategy = null
    this.selectedTokenStrategy = null
  }

  applyStrategy(strategyId: VoteOptimizationStrategyType, strategyManagerStore: StrategyManagerStore): void {
    const strategyResult = strategyManagerStore.getStrategyResult(strategyId)

    if (!strategyResult || !strategyResult.distribution) {
      return
    }

    this.resetSuperVoterAllocations()

    strategyResult.distribution.forEach(({ address, percentage }) => {
      const poolId = address.toString()
      this.updateSuperVoterPoolAllocation(poolId, percentage)
    })

    this.setLastAppliedStrategy(strategyId)
    this.setLastAppliedTokenStrategy(null)
    this.setPreviousAppliedTokenStrategy(null)
    this.lastTokenStrategyEstimatedAmount = 0
    this.setSelectedEngine("optimization", strategyId, undefined)
  }

  applyTokenStrategy(tokenAddress: LcAddress, strategyManagerStore: StrategyManagerStore): void {
    const result = strategyManagerStore.calculateTokenStrategyResult(tokenAddress)

    if (!result || !result.distribution) {
      return
    }

    this.resetSuperVoterAllocations()

    result.distribution.forEach(({ address, percentage }) => {
      const poolId = address.toString()
      this.updateSuperVoterPoolAllocation(poolId, percentage)
    })

    this.setLastAppliedTokenStrategy(tokenAddress)
    this.lastTokenStrategyEstimatedAmount = result.estimatedTokenAmount
    this.setLastAppliedStrategy(null)
    this.setPreviousAppliedStrategy(null)
    this.setSelectedEngine("token", undefined, tokenAddress)
  }

  setAvailableRevenueSourcesSearch(searchTerm: string): void {
    this.availableRevenueSourcesSearch = searchTerm
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-search`, searchTerm)
  }

  setAvailableRevenueSourcesAprFilterValue(value: number): void {
    this.availableRevenueSourcesAprFilterValue = value
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-aprValue`, value.toString())
  }

  setAvailableRevenueSourcesAprFilterMode(mode: "min" | "max"): void {
    this.availableRevenueSourcesAprFilterMode = mode
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-aprMode`, mode)
  }

  setAvailableRevenueSourcesTvlFilterValue(value: number): void {
    this.availableRevenueSourcesTvlFilterValue = value
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-tvlValue`, value.toString())
  }

  setAvailableRevenueSourcesTvlFilterMode(mode: "min" | "max"): void {
    this.availableRevenueSourcesTvlFilterMode = mode
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-tvlMode`, mode)
  }

  setAvailableRevenueSourcesRevenueFilterValue(value: number): void {
    this.availableRevenueSourcesRevenueFilterValue = value
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-revenueValue`, value.toString())
  }

  setAvailableRevenueSourcesRevenueFilterMode(mode: "min" | "max"): void {
    this.availableRevenueSourcesRevenueFilterMode = mode
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-revenueMode`, mode)
  }

  setListViewFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.listViewParamSources.urlParam === value) return
    this.listViewParamSources.urlParam = value
  }

  setListViewFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.listViewParamSources.localstorage === value) return
    this.listViewParamSources.localstorage = value
  }

  setListViewFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.listViewParamSources.ui === value) return
    this.listViewParamSources.ui = value

    // Auto-sync to localStorage only (not URL per design)
    this.storage.setItem(`${this.chainSlug}-vote-listView`, value.toString())
  }

  setAvailableRevenueSourcesSortByFromUi(value: Sort | undefined): void {
    if (!value) return
    if (
      this.availableRevenueSourcesSortByParamSources.ui?.key === value.key &&
      this.availableRevenueSourcesSortByParamSources.ui?.direction === value.direction &&
      this.availableRevenueSourcesSortByParamSources.ui?.type === value.type
    )
      return
    this.availableRevenueSourcesSortByParamSources.ui = value

    // Auto-sync to localStorage only
    this.storage.setItem(`${this.chainSlug}-vote-availableRevenueSources-sortBy`, JSON.stringify(value))
  }

  /**
   * Computed: APR filter interface implementation
   * Returns an IMinMaxFilter interface that provides access to filter state
   */
  get availableRevenueSourcesAprFilter(): IMinMaxFilter {
    const store = this
    return {
      get value() {
        return store.availableRevenueSourcesAprFilterValue
      },
      get mode() {
        return store.availableRevenueSourcesAprFilterMode
      },
      setValue: (value: number) => {
        store.setAvailableRevenueSourcesAprFilterValue(value)
      },
      setMode: (mode: "min" | "max") => {
        store.setAvailableRevenueSourcesAprFilterMode(mode)
      },
    }
  }

  /**
   * Computed: TVL filter interface implementation
   * Returns an IMinMaxFilter interface that provides access to filter state
   */
  get availableRevenueSourcesTvlFilter(): IMinMaxFilter {
    const store = this
    return {
      get value() {
        return store.availableRevenueSourcesTvlFilterValue
      },
      get mode() {
        return store.availableRevenueSourcesTvlFilterMode
      },
      setValue: (value: number) => {
        store.setAvailableRevenueSourcesTvlFilterValue(value)
      },
      setMode: (mode: "min" | "max") => {
        store.setAvailableRevenueSourcesTvlFilterMode(mode)
      },
    }
  }

  /**
   * Computed: Revenue filter interface implementation
   * Returns an IMinMaxFilter interface that provides access to filter state
   */
  get availableRevenueSourcesRevenueFilter(): IMinMaxFilter {
    const store = this
    return {
      get value() {
        return store.availableRevenueSourcesRevenueFilterValue
      },
      get mode() {
        return store.availableRevenueSourcesRevenueFilterMode
      },
      setValue: (value: number) => {
        store.setAvailableRevenueSourcesRevenueFilterValue(value)
      },
      setMode: (mode: "min" | "max") => {
        store.setAvailableRevenueSourcesRevenueFilterMode(mode)
      },
    }
  }

  /**
   * Computed: Available Revenue Sources - filtered and sorted
   * MobX automatically tracks all observables accessed (including nested revenueSource.displayName)
   * This ensures full reactivity when revenue sources connect later
   */
  get availableRevenueSources(): VotingGauge[] {
    // Filter by search term
    let filtered = this.votingGauges

    if (this.availableRevenueSourcesSearch.trim()) {
      const searchLower = this.availableRevenueSourcesSearch.toLowerCase()

      filtered = this.votingGauges.filter((vg) => {
        const gauge = vg.gauge
        // Accessing these properties ensures MobX tracks them for reactivity
        const displayName = gauge.revenueSource?.displayName?.toLowerCase() ?? ""
        const displayType = gauge.revenueSource?.displayType?.toLowerCase() ?? ""
        const tokens = gauge.revenueSource?.tokens ?? []
        const tokenSymbols = tokens.map((t) => t.symbol?.toLowerCase() ?? "").join(" ")

        return (
          displayName.includes(searchLower) || displayType.includes(searchLower) || tokenSymbols.includes(searchLower)
        )
      })
    }

    // Filter by APR (min or max)
    if (this.availableRevenueSourcesAprFilterValue > 0) {
      const filterValue = this.availableRevenueSourcesAprFilterValue
      const mode = this.availableRevenueSourcesAprFilterMode

      filtered = filtered.filter((vg) => {
        const apr = vg.apr // Access apr property - MobX tracks this
        if (mode === "min") {
          return apr >= filterValue
        }
        if (mode === "max") {
          return apr <= filterValue
        }
        return true // Default: no filter if mode is invalid
      })
    }

    // Filter by TVL (min or max)
    if (this.availableRevenueSourcesTvlFilterValue > 0) {
      const filterValue = this.availableRevenueSourcesTvlFilterValue
      const mode = this.availableRevenueSourcesTvlFilterMode
      filtered = filtered.filter((vg) => {
        const tvl = vg.tvlUsd // Access tvlUsd property - MobX tracks this
        if (mode === "min") {
          return tvl >= filterValue
        }
        if (mode === "max") {
          return tvl <= filterValue
        }
        return true // Default: no filter if mode is invalid
      })
    }

    // Filter by Revenue (min or max)
    if (this.availableRevenueSourcesRevenueFilterValue > 0) {
      const filterValue = this.availableRevenueSourcesRevenueFilterValue
      const mode = this.availableRevenueSourcesRevenueFilterMode
      filtered = filtered.filter((vg) => {
        const revenue = vg.fees // Access fees property - MobX tracks this
        if (mode === "min") {
          return revenue >= filterValue
        }
        if (mode === "max") {
          return revenue <= filterValue
        }
        return true // Default: no filter if mode is invalid
      })
    }

    // Apply user-selectable sort via TableProcessor
    const sortBy = this.availableRevenueSourcesSortBy
    const { rows } = TableProcessor({ rows: filtered, sort: sortBy })
    return rows
  }

  /**
   * Dispose of reactions and cleanup
   * Should be called when the store is no longer needed
   */
  dispose(): void {
    if (this.gaugeRepositoryReactionDisposer) {
      this.gaugeRepositoryReactionDisposer()
      this.gaugeRepositoryReactionDisposer = undefined
    }
  }
}
